/**
 * 지금 보고 있는 화면 공유 — 기기 공유 창(navigator.share)을 쓰거나 주소를 복사한다.
 * `nativeSheet` 가 false 면 기기 공유 창을 쓰지 않고 바로 복사한다 — PC 버튼용. 맥 Safari 는 PC 에서도 기기 공유 창
 * (AirDrop·Mail 메뉴)을 지원해서, 그대로 두면 브라우저마다 다르게 동작한다(2026-10-01 사용자 지적).
 * 주소에 utm 이 없을 때만 공유 표시(utm_source=share …)를 붙인다(세미나 상세·머리글 공유 버튼과 같은 규칙).
 */
export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'failed';

export interface ShareUtmOptions {
    /** utm_campaign — 어드민 캠페인(campaigns.campaign_key)과 맞아야 캠페인 화면에 잡힌다. 기본 `user_share` */
    campaign?: string;
    /** utm_content — 기본은 경로(`/posts/12` → `posts_12`). 값 종류가 끝없이 늘어나는 토큰 등은 넣지 않는다 */
    content?: string;
}

/**
 * 공유 주소에 공유 표시(utm_source=share&utm_medium=link&utm_campaign=…&utm_content=…)를 붙인다.
 * 주소에 utm_source 가 이미 있으면 건드리지 않는다. 주소를 읽지 못하면 받은 그대로 돌려준다.
 */
export const withShareUtm = (href: string, options: ShareUtmOptions = {}): string => {
    try {
        const url: URL = new URL(href);
        if (!url.searchParams.has('utm_source')) {
            url.searchParams.set('utm_source', 'share');
            url.searchParams.set('utm_medium', 'link');
            url.searchParams.set('utm_campaign', options.campaign ?? 'user_share');
            url.searchParams.set('utm_content', options.content ?? (url.pathname.replace(/^\/+|\/+$/g, '').replace(/\//g, '_') || 'home'));
        }
        return url.toString();
    } catch {
        return href;
    }
};

const shortLinks = new Map<string, Promise<string>>();
/** UTM을 먼저 붙인 원본을 서버에 보관한다. 실패해도 기존 긴 링크로 공유할 수 있다. */
export const shortShareUrl = (href: string, options: ShareUtmOptions = {}): Promise<string> => {
    const target = withShareUtm(href, options);
    if (/^https:\/\/www\.myungchisa\.com\/api\/s\/[A-Za-z0-9_-]{16}$/.test(href)) return Promise.resolve(href);
    const cached = shortLinks.get(target);
    if (cached) return cached;
    const pending = fetch('/api/short-links', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: target }), signal: AbortSignal.timeout(5000),
    }).then(async (response) => {
        if (!response.ok) throw new Error('short link unavailable');
        const body = await response.json();
        const url = body.result?.url;
        if (typeof url !== 'string' || !/^https:\/\/www\.myungchisa\.com\/api\/s\/[A-Za-z0-9_-]{16}$/.test(url)) throw new Error('invalid short link');
        return url;
    }).catch(() => { shortLinks.delete(target); return target; });
    shortLinks.set(target, pending);
    return pending;
};

/** 보이지 않는 글상자에 주소를 넣고 선택해 복사(document.execCommand) — 클립보드 API 가 거부될 때의 대안 */
const copyWithSelection = (text: string): boolean => {
    const area: HTMLTextAreaElement = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '0';
    area.style.opacity = '0';
    document.body.appendChild(area);
    try {
        area.select();
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        document.body.removeChild(area);
    }
};

export const shareCurrentPage = async (title: string, nativeSheet: boolean = true): Promise<ShareResult> => {
    const url: string = await shortShareUrl(window.location.href);
    if (nativeSheet && navigator.share) {
        try {
            await navigator.share({ title, url });
            return 'shared';
        } catch (error: unknown) {
            if ((error as { name?: string } | null)?.name === 'AbortError') return 'cancelled';
        }
    }
    try {
        await navigator.clipboard.writeText(url);
        return 'copied';
    } catch {
        // 클립보드 권한이 막힌 환경(앱 내 브라우저·보안 연결이 아닌 주소 등)은 예전 방식으로 한 번 더 시도한다
        return copyWithSelection(url) ? 'copied' : 'failed';
    }
};
